import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Segmented } from '../../shared/ui/segmented';
import { Select } from '../../shared/ui/select';
import {
  EMPTY_CODE,
  STARTER_PROMPT,
  KIND_LABEL,
  createLibraryItem,
  getLibraryVersion,
  libraryKeys,
  listLibrary,
  type TLibraryItem,
  type TLibraryKind,
} from '../../entities/library';

const schema = z
  .object({
    kind: z.enum(['code', 'prompt']),
    name: z.string().trim().min(1, 'Give it a name you will recognise'),
    startFrom: z.enum(['empty', 'copy']),
    copyFromId: z.string(),
  })
  .refine((values) => values.startFrom === 'empty' || values.copyFromId.length > 0, {
    message: 'Choose what to copy',
    path: ['copyFromId'],
  });

type TValues = z.infer<typeof schema>;

const KINDS = [
  { value: 'code' as const, label: KIND_LABEL.code },
  { value: 'prompt' as const, label: KIND_LABEL.prompt },
];

const START_FROM: Record<TLibraryKind, { value: 'empty' | 'copy'; label: string }[]> = {
  code: [
    { value: 'empty', label: 'A starter function' },
    { value: 'copy', label: 'A copy of other code' },
  ],
  prompt: [
    { value: 'empty', label: 'A starter: one system and one user message' },
    { value: 'copy', label: 'A copy of another prompt' },
  ],
};

const DESCRIPTION: Record<TLibraryKind, string> = {
  code: 'A function Code steps pin by version: (event, ctx) => the new event.',
  prompt: 'Messages AI steps pin by version; each step binds the variables to its own data.',
};

// Both kinds start at v1: a function that returns the event, or a system and a user message.
export const LibraryCreate = ({
  onCreated,
  kind: fixedKind,
}: {
  onCreated: (item: TLibraryItem) => void;
  kind?: TLibraryKind;
}) => {
  const [open, setOpen] = useState(false);
  const client = useQueryClient();
  const items = useQuery({ queryKey: libraryKeys.list(), queryFn: () => listLibrary() });

  const api = useApiForm<TValues>({
    schema,
    defaultValues: { kind: fixedKind ?? 'code', name: '', startFrom: 'empty', copyFromId: '' },
    onSubmit: async (values) => {
      const origin = (items.data?.data ?? []).find((item) => item.id === values.copyFromId);
      const copied =
        values.startFrom === 'copy' && origin
          ? await getLibraryVersion(origin.id, origin.latestVersion)
          : null;
      const body =
        values.kind === 'code'
          ? { code: copied?.code ?? EMPTY_CODE, ...(copied ? { signature: copied.signature } : {}) }
          : copied?.messages
            ? { messages: copied.messages }
            : { messages: STARTER_PROMPT };
      const item = await createLibraryItem({
        kind: values.kind,
        name: values.name,
        ...body,
        ...(copied ? { comment: `Copied from ${origin?.name} v${copied.version}` } : {}),
      });
      await client.invalidateQueries({ queryKey: libraryKeys.all });
      setOpen(false);
      onCreated(item);
    },
  });

  const kind = api.form.watch('kind');
  const startFrom = api.form.watch('startFrom');
  const copyOptions = (items.data?.data ?? [])
    .filter((item) => item.kind === kind && item.latestVersion > 0)
    .map((item) => ({ value: item.id, label: `${item.name} · v${item.latestVersion}` }));

  const show = (next: boolean) => {
    if (next) api.form.reset();
    setOpen(next);
  };

  return (
    <>
      <Button size="sm" onClick={() => show(true)}>
        {fixedKind ? `New ${KIND_LABEL[fixedKind].toLowerCase()}` : 'New item'}
      </Button>

      <Modal
        open={open}
        onOpenChange={show}
        width="md"
        title="New in the Library"
        description={DESCRIPTION[kind]}
        footerNote="Opens the editor at v1"
        submit={{
          label: 'Create and open the editor',
          onSubmit: () => void api.submit(),
          waiting: api.form.formState.isSubmitting,
          waitingLabel: 'Creating',
        }}
      >
        <ApiForm api={api}>
          {!fixedKind && (
            <Field<TValues> name="kind" label="Kind">
              <Controller
                name="kind"
                control={api.form.control}
                render={({ field }) => (
                  <Segmented
                    aria-label="Kind"
                    fill
                    segments={KINDS}
                    value={field.value}
                    onChange={(next) => {
                      field.onChange(next);
                      api.form.setValue('copyFromId', '');
                    }}
                  />
                )}
              />
            </Field>
          )}

          <Field<TValues> name="name" label="Name">
            <TextInput placeholder={kind === 'code' ? 'fmt-billing' : 'Support triage'} autoFocus />
          </Field>

          <Field<TValues> name="startFrom" label="Start from">
            <Controller
              name="startFrom"
              control={api.form.control}
              render={({ field }) => (
                <Segmented
                  aria-label="Start from"
                  fill
                  segments={START_FROM[kind]}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>

          {startFrom === 'copy' && (
            <Field<TValues>
              name="copyFromId"
              label="Copy from"
              hint={
                copyOptions.length === 0
                  ? 'Nothing of this kind has a version to copy yet — start blank'
                  : 'Its latest version becomes v1 here; the original is untouched'
              }
            >
              <Controller
                name="copyFromId"
                control={api.form.control}
                render={({ field }) => (
                  <Select
                    aria-label="Copy from"
                    value={field.value}
                    onChange={field.onChange}
                    options={copyOptions}
                    placeholder="Choose what to copy"
                    disabled={copyOptions.length === 0}
                  />
                )}
              />
            </Field>
          )}

          <Note>
            It runs nowhere until a step in a flow pins one of its versions. Creating it changes
            nothing on its own.
          </Note>

          {/* The submit button lives in the footer, outside the form; without this one Enter would do nothing. */}
          <button type="submit" className="hidden" aria-hidden />
        </ApiForm>
      </Modal>
    </>
  );
};
